import React, { useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Share2, Copy, Check, MessageCircle, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { normaliseWhatsAppPhone } from "./WhatsAppShareButton";

/**
 * Generic share popover — opens a dialog that lets the user share a snippet of
 * text (with optional link) via WhatsApp, email, or copy-to-clipboard.
 *
 * Unlike WhatsAppShareButton (which is invoice-specific), this works for any
 * page — Distributor Hub snapshot, Day Book summary, Sales Register summary etc.
 *
 * Props:
 *   text     — initial message body (editable in the dialog)
 *   link     — optional URL appended to the message
 *   subject  — initial email subject
 *   label    — button label (default "Share")
 *   testId   — base test id
 *   className — additional classes on the trigger button
 */
export default function SharePopover({
    text = "",
    link = "",
    subject = "",
    label = "Share",
    testId = "share-popover",
    className = "",
}) {
    const [open, setOpen] = useState(false);
    const [phone, setPhone] = useState("");
    const [emailTo, setEmailTo] = useState("");
    const [emailSubject, setEmailSubject] = useState(subject);
    const [body, setBody] = useState(link ? `${text}\n\n${link}` : text);
    const [copied, setCopied] = useState(false);

    const waLink = (() => {
        const digits = normaliseWhatsAppPhone(phone);
        const encoded = encodeURIComponent(body);
        return digits ? `https://wa.me/${digits}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
    })();

    const mailLink = `mailto:${encodeURIComponent(emailTo)}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(body)}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(body);
            setCopied(true);
            toast.success("Copied to clipboard");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Couldn't copy — long-press to copy");
        }
    };

    return (
        <>
            <Button
                onClick={() => setOpen(true)}
                variant="outline"
                className={`rounded-none border-stone-300 ${className}`}
                data-testid={testId}
            >
                <Share2 className="w-4 h-4 mr-2" />{label}
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-none max-w-lg" data-testid={`${testId}-dialog`}>
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                            <Share2 className="w-5 h-5 text-sky-700" />Share
                        </DialogTitle>
                        <DialogDescription className="text-xs text-stone-500">
                            WhatsApp opens with the message pre-filled · email opens your default mail app · or just copy.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 mt-2">
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Message</Label>
                            <Textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                rows={6}
                                className="rounded-none border-stone-300 mt-1 text-xs"
                                data-testid={`${testId}-body`}
                            />
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">WhatsApp phone</Label>
                                <Input
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="+91 98765 43210 (optional)"
                                    className="rounded-none border-stone-300 mt-1 font-mono"
                                    data-testid={`${testId}-wa-phone`}
                                />
                            </div>
                            <div>
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Email to</Label>
                                <Input
                                    type="email"
                                    value={emailTo}
                                    onChange={(e) => setEmailTo(e.target.value)}
                                    placeholder="accounts@buyer.com"
                                    className="rounded-none border-stone-300 mt-1"
                                    data-testid={`${testId}-email-to`}
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Email subject</Label>
                            <Input
                                value={emailSubject}
                                onChange={(e) => setEmailSubject(e.target.value)}
                                className="rounded-none border-stone-300 mt-1"
                                data-testid={`${testId}-email-subject`}
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2 flex-wrap">
                        <Button variant="outline" onClick={copy} className="rounded-none" data-testid={`${testId}-copy`}>
                            {copied ? <><Check className="w-4 h-4 mr-2" />Copied</> : <><Copy className="w-4 h-4 mr-2" />Copy</>}
                        </Button>
                        <a
                            href={mailLink}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-none bg-stone-700 hover:bg-stone-800 text-white inline-flex items-center justify-center px-4 py-2 text-sm"
                            data-testid={`${testId}-mail`}
                            onClick={() => toast.success("Opening mail app…")}
                        >
                            <Mail className="w-4 h-4 mr-2" />Email
                        </a>
                        <a
                            href={waLink}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center justify-center px-4 py-2 text-sm"
                            data-testid={`${testId}-wa`}
                            onClick={() => toast.success("Opening WhatsApp…")}
                        >
                            <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
                        </a>
                        {link && (
                            <a
                                href={link}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-none bg-sky-700 hover:bg-sky-800 text-white inline-flex items-center justify-center px-4 py-2 text-sm"
                                data-testid={`${testId}-open-link`}
                            >
                                <ExternalLink className="w-4 h-4 mr-2" />Open link
                            </a>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
